// src/components/files/PieceJointeGallery.tsx — SCE Issues
// Vignettes des pièces jointes : rangée sous un événement de l'activité, et
// briques du bloc Photos (BlocPhotos). Un clic ouvre la visionneuse de la
// fiche (Visionneuse), qui fait défiler TOUTES les photos du problème.
//
// ─── Images : l'aperçu SharePoint d'abord ───
//
// Les photos s'affichaient sur PC mais pas sur iPhone (retour du 25/09) :
// l'adresse de téléchargement n'est pas faite pour <img> (session SharePoint
// requise quand Graph ne la renvoie pas, ou fichier servi « à télécharger »,
// que Safari refuse d'afficher). On passe donc par les aperçus générés par
// SharePoint — adresses prévues pour l'affichage, en JPEG, plus légères en
// 4G — et on retombe sur l'adresse de téléchargement s'ils manquent.
//
// ─── Puis la meilleure qualité (22/10) ───
//
// Les captures d'écran étaient floues : la vignette `medium` fait 176 px de
// côté long (une capture 16:9 n'a que 99 px de haut, étirés dans une tuile de
// 124 px sur un écran ×2), l'aperçu `large` 800 px en JPEG. L'image affichée
// d'abord reste la plus légère ; une version plus nette (`nette` : l'aperçu
// large pour une vignette, l'original pour la visionneuse) se charge à côté et
// la remplace une fois prête, sauf connexion lente (connexionLente).
//
// ─── Fichiers : leur nom d'origine (12/10) ───
//
// Un PDF n'a pas d'aperçu parlant : une grande vignette (bloc Photos) écrit son
// nom sous l'icône ; dans l'activité (vignettes de 48 px), il devient une
// étiquette icône + nom. Le nom d'origine est celui du fichier stocké, débarrassé
// de l'horodatage et des marques ajoutés à l'envoi (nomAffiche) : pas de colonne.

import { useEffect, useState, type ReactNode } from 'react'
import type React from 'react'
import { FileText, Film, ImageOff } from 'lucide-react'
import { connexionLente, type PieceJointe } from '@/api/files.api'
import { nomAffiche } from '@/utils/rattachementPhotos'

// ─── Types ───

interface PieceJointeGalleryProps {
  piecesJointes: PieceJointe[]
  /** Ouvre la visionneuse de la fiche sur cette pièce jointe. */
  onOuvrir: (pj: PieceJointe) => void
  /** Taille des thumbnails en px (défaut 48) */
  thumbSize?: number
}

// ─── Image : essaie chaque adresse, dans l'ordre ───

/**
 * Affiche la première adresse qui se charge. Un aperçu peut manquer (image
 * tout juste envoyée, aperçu pas encore généré) ou avoir expiré : on passe à
 * la suivante. Plus rien : une icône plutôt qu'une image cassée.
 */
export function ImageAvecRepli({
  sources,
  nette,
  alt,
  style,
  onLoad,
}: {
  sources: (string | undefined)[]
  /** Version plus nette, chargée à côté : remplace l'image une fois prête. */
  nette?: string
  alt: string
  style: React.CSSProperties
  onLoad?: (img: HTMLImageElement) => void
}): JSX.Element {
  const liste = sources.filter((u): u is string => !!u)
  const [rang, setRang] = useState(0)
  const [netteChargee, setNetteChargee] = useState<string | null>(null)
  useEffect(() => {
    if (!nette) return
    // Décodée avant d'être montrée : pas de blanc au remplacement. Illisible
    // (HEIC hors Safari, adresse expirée) : on garde l'image d'avant.
    let annule = false
    const img = new Image()
    img.onload = () => {
      if (!annule) setNetteChargee(nette)
    }
    img.src = nette
    return () => {
      annule = true
    }
  }, [nette])
  const src = nette && netteChargee === nette ? nette : liste[rang]
  if (!src) {
    return (
      <span className="flex items-center justify-center" style={{ ...style, color: 'var(--color-gray-70)' }}>
        <ImageOff size={20} aria-label={`${alt} : aperçu indisponible`} />
      </span>
    )
  }
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      style={style}
      onError={() => (src === netteChargee ? setNetteChargee(null) : setRang((r) => r + 1))}
      {...(onLoad ? { onLoad: (e: React.SyntheticEvent<HTMLImageElement>) => onLoad(e.currentTarget) } : {})}
    />
  )
}

// ─── Rangée de vignettes ───

export function PieceJointeGallery({
  piecesJointes,
  onOuvrir,
  thumbSize = 48,
}: PieceJointeGalleryProps): JSX.Element | null {
  if (piecesJointes.length === 0) return null
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
      {piecesJointes.map((pj) =>
        // Une petite vignette ne peut pas porter de nom : un PDF ou un autre
        // fichier s'affiche en étiquette, icône + nom d'origine (12/10).
        pj.isImage || pj.isVideo || thumbSize >= TAILLE_AVEC_NOM ? (
          <Vignette key={pj.id} pj={pj} size={thumbSize} onClick={() => onOuvrir(pj)} />
        ) : (
          <EtiquetteFichier key={pj.id} pj={pj} hauteur={thumbSize} onClick={() => onOuvrir(pj)} />
        ),
      )}
    </div>
  )
}

/** Un fichier (PDF…) en étiquette : icône et nom d'origine, à la hauteur des vignettes. */
function EtiquetteFichier({ pj, hauteur, onClick }: { pj: PieceJointe; hauteur: number; onClick: () => void }): JSX.Element {
  const nom = nomAffiche(pj.nom)
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Ouvrir ${nom}`}
      title={nom}
      className="flex items-center"
      style={{
        height: hauteur,
        maxWidth: 260,
        gap: 8,
        padding: '0 12px 0 10px',
        borderRadius: 6,
        backgroundColor: 'var(--color-gray-5)',
        border: '1px solid var(--color-border)',
        cursor: 'pointer',
        minWidth: 0,
      }}
    >
      <FileText size={20} color={pj.isPdf ? 'var(--color-traffic-red)' : 'var(--color-gray-60)'} style={{ flexShrink: 0 }} />
      <span
        style={{
          fontSize: 'var(--font-size-info)',
          color: 'var(--color-black)',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          minWidth: 0,
        }}
      >
        {nom}
      </span>
    </button>
  )
}

/** À partir de cette taille, une vignette de fichier écrit son nom sous l'icône. */
const TAILLE_AVEC_NOM = 80

// ─── Vignette ───

/** Une pièce jointe en carré : aperçu d'une photo, icône pour le reste. */
export function Vignette({
  pj,
  size,
  onClick,
  libelle,
  badges,
  rayon = 6,
}: {
  pj: PieceJointe
  size: number
  onClick: () => void
  /** Nom accessible (défaut : « Ouvrir <nom> »). */
  libelle?: string
  /** Pastilles posées sur la vignette (annotée, initiales). */
  badges?: ReactNode
  rayon?: number
}): JSX.Element {
  const iconSize = size > 36 ? 20 : 14
  // Le nom d'origine, sans l'horodatage ni les marques ajoutés à l'envoi.
  const nom = nomAffiche(pj.nom)
  const avecNom = !pj.isImage && size >= TAILLE_AVEC_NOM

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={libelle ?? `Ouvrir ${nom}`}
      title={nom}
      style={{
        width: size,
        height: size,
        borderRadius: rayon,
        overflow: 'hidden',
        backgroundColor: 'var(--color-gray-5)',
        border: '1px solid var(--color-border)',
        padding: 0,
        cursor: 'pointer',
        flexShrink: 0,
        position: 'relative',
      }}
    >
      {pj.isImage ? (
        <ImageAvecRepli
          sources={[pj.vignette, pj.apercu, pj.url]}
          {...(pj.vignette && pj.apercu && !connexionLente() ? { nette: pj.apercu } : {})}
          alt={pj.nom}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: avecNom ? 6 : 0,
            padding: avecNom ? '8px 6px' : 0,
            boxSizing: 'border-box',
          }}
        >
          {pj.isPdf ? (
            <FileText size={avecNom ? 28 : iconSize} color="var(--color-traffic-red)" style={{ flexShrink: 0 }} />
          ) : pj.isVideo ? (
            <Film size={avecNom ? 28 : iconSize} color="var(--color-gray-70)" style={{ flexShrink: 0 }} />
          ) : (
            <FileText size={avecNom ? 28 : iconSize} color="var(--color-gray-60)" style={{ flexShrink: 0 }} />
          )}
          {/* Grande vignette : le nom d'origine sous l'icône, sur deux lignes au plus */}
          {avecNom && (
            <span
              style={{
                fontSize: 11,
                lineHeight: '14px',
                color: 'var(--color-black)',
                textAlign: 'center',
                overflowWrap: 'anywhere',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: size >= 110 ? 3 : 2,
                overflow: 'hidden',
              }}
            >
              {nom}
            </span>
          )}
        </div>
      )}
      {badges}
    </button>
  )
}
